// src/components/MaintenanceLists/TCOCalculator.js
import { Timestamp } from 'firebase/firestore';

/**
 * Calculate TCO (Total Cost of Ownership) for a motorcycle
 * 
 * @param {Object} bike - The bike data object
 * @returns {Object|null} TCO calculations or null if bike data is invalid
 */
export const calculateTCO = (bike) => {
  if (!bike) return null;

  const purchaseDate = bike.purchaseDate instanceof Timestamp ? 
    bike.purchaseDate.toDate() : new Date(bike.purchaseDate);
  const today = new Date();
  const ownershipDays = Math.max(1, Math.floor((today - purchaseDate) / (1000 * 60 * 60 * 24)));
  const ownershipMonths = ownershipDays / 30.44; // Average month length
  const ownershipYears = ownershipDays / 365.25; // Account for leap years

  // Calculate operational costs
  let maintenanceCosts = 0;
  let operatingCosts = 0;
  let upgradeCosts = 0;
  let latestMileage = 0;
  const initialMileage = bike.initialMileage || 0;

  if (bike.maintenanceItems) {
    bike.maintenanceItems.forEach(item => {
      // Check odometer readings from both maintenance and observation records
      if (item.odometer) {
        latestMileage = Math.max(latestMileage, item.odometer);
      }

      // Only sum costs from non-observation records
      if (!item.type || item.type !== 'observation') {
        const cost = parseFloat(item.cost) || 0;
        switch (item.category) {
          case 'operating':
            operatingCosts += cost;
            break;
          case 'upgrades':
            upgradeCosts += cost;
            break;
          default:
            maintenanceCosts += cost;
        }
      }
    });
  }

  const totalOperationalCosts = maintenanceCosts + operatingCosts;
  const totalCostsWithUpgrades = totalOperationalCosts + upgradeCosts;
  
  // Calculate time-based costs
  const yearlyOperational = totalOperationalCosts / ownershipYears;
  const yearlyTotal = totalCostsWithUpgrades / ownershipYears;
  const monthlyOperational = totalOperationalCosts / ownershipMonths;
  const monthlyTotal = totalCostsWithUpgrades / ownershipMonths;

  // Calculate total miles driven during ownership
  const milesDriven = latestMileage > initialMileage ? latestMileage - initialMileage : 0;

  // Calculate per-mile costs using actual miles driven
  const costPerMile = milesDriven > 0 ? {
    operational: totalOperationalCosts / milesDriven,
    total: totalCostsWithUpgrades / milesDriven
  } : null;

  return {
    maintenanceCosts,
    operatingCosts,
    upgradeCosts,
    totalOperationalCosts,
    totalCostsWithUpgrades,
    monthlyOperational,
    monthlyTotal,
    yearlyOperational,
    yearlyTotal,
    ownershipDays,
    ownershipMonths,
    ownershipYears,
    initialMileage,
    latestMileage,
    milesDriven,
    costPerMile
  };
};

/**
 * Process expense data into monthly totals
 * 
 * @param {Object} bike - The bike data object
 * @returns {Object|null} Monthly expense data or null if bike data is invalid
 */
export const processMonthlyExpenses = (bike) => {
  if (!bike || !bike.maintenanceItems || !bike.purchaseDate) return null;

  // Get start and end dates
  const startDate = bike.purchaseDate instanceof Timestamp ? 
    bike.purchaseDate.toDate() : new Date(bike.purchaseDate);
  const endDate = new Date();

  // Create array of all months between start and end dates
  const months = [];
  const monthlyExpenses = [];
  let cumulativeTotal = 0;
  const cumulativeExpenses = [];

  let currentDate = new Date(startDate);
  currentDate.setDate(1); // Set to first of month for consistent comparison

  while (currentDate <= endDate) {
    const monthKey = currentDate.toLocaleString('en-GB', { month: 'short', year: '2-digit' });
    months.push(monthKey);

    // Calculate total expenses for this month
    const monthlyTotal = bike.maintenanceItems.reduce((total, item) => {
      if (item.type === 'observation') return total; // Skip observation records
      
      const itemDate = item.date instanceof Timestamp ? 
        item.date.toDate() : new Date(item.date);
      
      if (itemDate.getMonth() === currentDate.getMonth() && 
          itemDate.getFullYear() === currentDate.getFullYear()) {
        return total + (parseFloat(item.cost) || 0);
      }
      return total;
    }, 0);

    monthlyExpenses.push(monthlyTotal);
    cumulativeTotal += monthlyTotal;
    cumulativeExpenses.push(cumulativeTotal);

    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return {
    labels: months,
    expenses: monthlyExpenses,
    cumulative: cumulativeExpenses
  };
};